import BaseEntity from '@/editor/entities/BaseEntity';
import { cloneDeep, isNumber } from 'lodash-es';
import { EEntityType } from '../types';
import {
  EFaceDataBaseType,
  IBaseDataArcEdgeFace,
  IBaseDataContourFace,
  IBaseDataCylinderFace,
  IBaseDataEdgeFace,
  IBaseDataHoleFace,
  IFaceData
} from '../data/face/face';
import { EdgeFaceModel } from './3d/faceModel/edgeFaceModel';
import { ContourFaceModel } from './3d/faceModel/contourFaceModel';
import { HoleFaceModel } from './3d/faceModel/holeFaceModel';
import { Point2dUtils } from '../math/Point2dUtils';
import RegionModel from './2d/regionModel';
import { isSimilarFaceHoles } from '../utils/face';
import { CylinderFaceModel } from './3d/faceModel/cylinderFaceModel';
import { ArcEdgeFaceModel } from './3d/faceModel/arcEdgeFaceModel';
import { equalOutlines } from '../math/utils';
import { ArcHoleFaceModel } from './3d/faceModel/arcHoleFaceModel';

type IFaceModel3d =
  | EdgeFaceModel
  | ArcEdgeFaceModel
  | ContourFaceModel
  | HoleFaceModel
  | CylinderFaceModel
  | ArcHoleFaceModel;

class FaceEntity extends BaseEntity<EEntityType.Face> {
  private _faceData: IFaceData;
  protected override _model3d: IFaceModel3d | null = null;

  protected override _model2d: RegionModel | null = null;

  private _created = false;

  constructor(faceData: IFaceData) {
    super({
      type: EEntityType.Face,
      id: faceData.id
    });

    this._faceData = cloneDeep(faceData);

    switch (this._faceData.baseDataType) {
      case EFaceDataBaseType.Edge:
        this._model3d = new EdgeFaceModel({ id: this._faceData.id });
        break;
      case EFaceDataBaseType.ArcEdge:
        this._model3d = new ArcEdgeFaceModel({ id: this._faceData.id });
        break;
      case EFaceDataBaseType.Contour:
        this._model3d = new ContourFaceModel({ id: this._faceData.id });
        break;
      case EFaceDataBaseType.Hole: {
        const baseData = this._faceData.baseData as IBaseDataHoleFace;
        if (baseData.arcCenter) {
          this._model3d = new ArcHoleFaceModel({ id: this._faceData.id });
        } else {
          this._model3d = new HoleFaceModel({ id: this._faceData.id });
        }
        break;
      }
      case EFaceDataBaseType.Cylinder:
        this._model3d = new CylinderFaceModel({ id: this._faceData.id });
        break;
    }

    this.update(faceData);
  }

  // setVisible(visible: boolean) {
  //   if (!this._model3d || this.visible === visible) return;
  //   this._model3d.setVisible(visible);
  //   this.visible = visible;
  // }

  getData() {
    return this._faceData;
  }

  get model() {
    return this._model3d;
  }

  override update(params: IFaceData & { forceUpdate?: boolean }) {
    if (!this._model3d) return;

    let destroyUpdate = !this._created || params.forceUpdate;
    let noDestroyUpdate = false;

    switch (this._faceData.baseDataType) {
      case EFaceDataBaseType.Edge:
        {
          const oldFaceBaseData = this._faceData.baseData as IBaseDataEdgeFace;
          const newFaceBaseData = params.baseData as IBaseDataEdgeFace;

          if (!Point2dUtils.equals(newFaceBaseData.start, oldFaceBaseData.start)) {
            oldFaceBaseData.start = newFaceBaseData.start;
            destroyUpdate = true;
          }

          if (!Point2dUtils.equals(newFaceBaseData.end, oldFaceBaseData.end)) {
            oldFaceBaseData.end = newFaceBaseData.end;
            destroyUpdate = true;
          }

          if (newFaceBaseData.height !== oldFaceBaseData.height) {
            oldFaceBaseData.height = newFaceBaseData.height;
            destroyUpdate = true;
          }

          if (newFaceBaseData.offGround !== oldFaceBaseData.offGround) {
            oldFaceBaseData.offGround = newFaceBaseData.offGround;
            destroyUpdate = true;
          }

          if (!isSimilarFaceHoles(params.holes, this._faceData.holes)) {
            this._faceData.holes = params.holes;
            destroyUpdate = true;
          }
        }
        break;
      case EFaceDataBaseType.ArcEdge:
        {
          const oldFaceBaseData = this._faceData.baseData as IBaseDataArcEdgeFace;
          const newFaceBaseData = params.baseData as IBaseDataArcEdgeFace;

          if (!Point2dUtils.equals(newFaceBaseData.start, oldFaceBaseData.start)) {
            oldFaceBaseData.start = newFaceBaseData.start;
            destroyUpdate = true;
          }

          if (!Point2dUtils.equals(newFaceBaseData.end, oldFaceBaseData.end)) {
            oldFaceBaseData.end = newFaceBaseData.end;
            destroyUpdate = true;
          }

          if (newFaceBaseData.height !== oldFaceBaseData.height) {
            oldFaceBaseData.height = newFaceBaseData.height;
            destroyUpdate = true;
          }

          if (newFaceBaseData.offGround !== oldFaceBaseData.offGround) {
            oldFaceBaseData.offGround = newFaceBaseData.offGround;
            destroyUpdate = true;
          }

          if (
            newFaceBaseData.center &&
            oldFaceBaseData.center &&
            !Point2dUtils.equals(newFaceBaseData.center, oldFaceBaseData.center)
          ) {
            oldFaceBaseData.center = newFaceBaseData.center;
            destroyUpdate = true;
          }

          if (newFaceBaseData.clockwise !== oldFaceBaseData.clockwise) {
            oldFaceBaseData.clockwise = newFaceBaseData.clockwise;
            destroyUpdate = true;
          }

          if (!isSimilarFaceHoles(params.holes, this._faceData.holes)) {
            this._faceData.holes = params.holes;
            destroyUpdate = true;
          }
        }
        break;
      case EFaceDataBaseType.Contour:
        {
          const oldFaceBaseData = this._faceData.baseData as IBaseDataContourFace;
          const newFaceBaseData = params.baseData as IBaseDataContourFace;

          if (newFaceBaseData.contour && !equalOutlines([newFaceBaseData.contour], [oldFaceBaseData.contour])) {
            oldFaceBaseData.contour = newFaceBaseData.contour;
            destroyUpdate = true;
          }

          if (params.holes && !isSimilarFaceHoles(params.holes, this._faceData.holes)) {
            this._faceData.holes = params.holes;
            destroyUpdate = true;
          }
          if (isNumber(newFaceBaseData.offGround) && newFaceBaseData.offGround !== oldFaceBaseData.offGround) {
            oldFaceBaseData.offGround = newFaceBaseData.offGround;
            destroyUpdate = true;
          }
        }
        break;
      case EFaceDataBaseType.Hole:
        {
          const newFaceBaseData = params.baseData as IBaseDataHoleFace;
          const oldFaceBaseData = this._faceData.baseData as IBaseDataHoleFace;
          if (
            newFaceBaseData.width !== oldFaceBaseData.width ||
            newFaceBaseData.thickness !== oldFaceBaseData.thickness ||
            newFaceBaseData.height !== oldFaceBaseData.height ||
            newFaceBaseData.offGround !== oldFaceBaseData.offGround ||
            newFaceBaseData.rotation !== oldFaceBaseData.rotation ||
            !Point2dUtils.equals(newFaceBaseData.center, oldFaceBaseData.center)
          ) {
            this._faceData.baseData = { ...newFaceBaseData };
            noDestroyUpdate = true;
          }

          const arcCenter = newFaceBaseData.arcCenter;
          if (arcCenter && noDestroyUpdate) {
            oldFaceBaseData.arcCenter = arcCenter;
            destroyUpdate = true;
          }
        }
        break;
      case EFaceDataBaseType.Cylinder:
        {
          const newFaceBaseData = params.baseData as IBaseDataCylinderFace;
          const oldFaceBaseData = this._faceData.baseData as IBaseDataCylinderFace;
          if (
            !Point2dUtils.equals(newFaceBaseData.center, oldFaceBaseData.center) ||
            (isNumber(newFaceBaseData.radius) && newFaceBaseData.radius !== oldFaceBaseData.radius) ||
            (isNumber(newFaceBaseData.height) && newFaceBaseData.height !== oldFaceBaseData.height) ||
            (isNumber(newFaceBaseData.offGround) && newFaceBaseData.offGround !== oldFaceBaseData.offGround)
          ) {
            this._faceData.baseData = { ...newFaceBaseData };
            destroyUpdate = true;
          }
        }
        break;
    }

    if (this._faceData.materialInfo.id !== params.materialInfo.id) {
      this._faceData.materialInfo = params.materialInfo;
      noDestroyUpdate = true;
    }

    if (destroyUpdate) {
      this.clear();

      this._model3d.createModel(this._faceData);
      this._created = true;
    } else if (noDestroyUpdate) {
      this._model3d.updateWithoutDestroy(this._faceData);
    }
  }

  clear() {
    this._model3d?.clearModel();
    this._created = false;
  }
}

export default FaceEntity;
